/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { agentUpsertAiScanTarget } from "../funcs/agentUpsertAiScanTarget.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AiScanTargetMutationResult } from "../models/components/aiscantargetmutationresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpsertAiScanTargetRequest,
  UpsertAiScanTargetSecurity,
} from "../models/operations/upsertaiscantarget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpsertAiScanTargetMutationVariables = {
  request: UpsertAiScanTargetRequest;
  security?: UpsertAiScanTargetSecurity | undefined;
  options?: RequestOptions;
};

export type UpsertAiScanTargetMutationData = AiScanTargetMutationResult;

export type UpsertAiScanTargetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * upsertAiScanTarget agent
 *
 * @remarks
 * Add a scan target for this organization or replace one it added earlier. Built-in targets are system-supplied and read-only: a write under a built-in's id is accepted only when it carries that built-in's definition unchanged. Every field is a full replacement except gateway_client, which an existing target keeps when the field is omitted, so a write need not restate the target's matchers; sending gateway_client with empty lists still clears them. Clearing a target's last verifiable matcher also clears any access decision recorded about it, since nothing could enforce it any more; the organization decides again once the target can be recognized at the gateway. Agents pick the change up on their next policy poll. Requires a session with the org:admin scope.
 */
export function useUpsertAiScanTargetMutation(
  options?: MutationHookOptions<
    UpsertAiScanTargetMutationData,
    UpsertAiScanTargetMutationError,
    UpsertAiScanTargetMutationVariables
  >,
): UseMutationResult<
  UpsertAiScanTargetMutationData,
  UpsertAiScanTargetMutationError,
  UpsertAiScanTargetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpsertAiScanTargetMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpsertAiScanTarget(): MutationKey {
  return ["@gram/client", "agent", "upsertAiScanTarget"];
}

export function buildUpsertAiScanTargetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpsertAiScanTargetMutationVariables,
  ) => Promise<UpsertAiScanTargetMutationData>;
} {
  return {
    mutationKey: mutationKeyUpsertAiScanTarget(),
    mutationFn: function upsertAiScanTargetMutationFn({
      request,
      security,
      options,
    }): Promise<UpsertAiScanTargetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(agentUpsertAiScanTarget(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
